import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { signedOut } from '../../auth/store/authSlice.js';
import { lessonCompleted, lessonFeedbackDismissed } from '../../lesson/store/lessonSlice.js';
import { fetchProctorDetails } from '../api/proctoringApi.js';
import { matchProctorConfig, getProctorElementIds } from '../lib/proctoringRules.js';
import { stopRemoteProctoring } from '../lib/remoteProctoring.js';
import {
  proctorCheckRequested,
  proctorNotRequired,
  proctorRequired,
  proctorStopped,
} from './proctorSlice.js';
import { captureSagaError } from '../../../lib/sentry.js';

function getOrgId(state) {
  return state.auth?.org?.id
    ?? state.auth?.user?.user_orgid
    ?? state.auth?.user?.org_id
    ?? localStorage.getItem('orgID')
    ?? null;
}

export function* handleProctorCheck(action) {
  const { courseId, elementId, resolvedElementId } = action.payload;
  try {
    const orgId = yield select(getOrgId);
    const list = yield call(fetchProctorDetails, { courseId, orgId });
    const items = Array.isArray(list) ? list : list?.data ?? [];
    localStorage.setItem('proctorList', JSON.stringify(getProctorElementIds(items)));

    const match = matchProctorConfig(items, { courseId, elementId, resolvedElementId, orgId });
    if (!match) {
      yield put(proctorNotRequired({ courseId, elementId, resolvedElementId }));
      return;
    }

    yield put(proctorRequired({
      courseId,
      elementId,
      resolvedElementId,
      eventName: match.proctor_event_name,
      matchedElementId: match.element_id,
    }));
  } catch (err) {
    captureSagaError(err, { action: 'proctor/proctorCheckRequested', courseId, elementId });
    yield put(proctorNotRequired({ courseId, elementId, resolvedElementId }));
  }
}

export function* handleProctorStop() {
  try {
    const activeInStore = yield select((state) =>
      state.proctor?.status === 'active' || state.proctor?.status === 'starting');
    if (activeInStore || (typeof window !== 'undefined' && window.proctorStarted)) {
      yield call(stopRemoteProctoring);
    }
  } catch (err) {
    captureSagaError(err, { action: 'proctor/stopRemoteProctoring' });
  } finally {
    yield put(proctorStopped());
  }
}

export function* handleProctorFeedbackCompletion() {
  const shouldStop = yield select((state) =>
    state.lesson?.status === 'complete' && state.proctor?.status === 'active');
  if (shouldStop) yield call(handleProctorStop);
}

export default function* proctorSaga() {
  yield takeLatest(proctorCheckRequested.type, handleProctorCheck);
  yield takeEvery([lessonCompleted.type, signedOut.type], handleProctorStop);
  yield takeEvery(lessonFeedbackDismissed.type, handleProctorFeedbackCompletion);
}
